// LoginComponent.jsx
import { Link } from "react-router-dom";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import React, { useState, useRef, useEffect } from "react";
import baseUrl from "../../base_url";
// import Loader from "../home_components/loader";

const Loader = () => {
  return (
    <div className="relative w-[100px] h-[24px] overflow-hidden">
      <div className="absolute inset-0 flex items-center justify-center z-10 text-sm font-medium text-white">
        Loading...
      </div>
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/50 to-transparent animate-loader" />
    </div>
  );
};

function LoginComponent() {
    const [isLoading, setIsLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [formError, setFormError] = useState({
        hasError: false,
        message: "",
        field: null 
    });
    
    const emailRef = useRef(null);
    const passwordRef = useRef(null);
    
    const navigate = useNavigate();
    
    const passwordFieldType = showPassword ? "text" : "password";
    
    // Animation state
    const [isVisible, setIsVisible] = useState(false);
    
    useEffect(() => {
        setIsVisible(true); 
    }, []);
    
    // Form validation
    const validateForm = () => {
        const email = emailRef.current.value;
        const password = passwordRef.current.value;
        
        // Email validation - improved regex for better email validation
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (!emailRegex.test(email)) {
            setFormError({
                hasError: true,
                message: "Please enter a valid email address",
                field: "email"
            });
            return false;
        }
        
        // Password validation
        if (password.length < 6) {
            setFormError({
                hasError: true,
                message: "Password must be at least 6 characters",
                field: "password"
            });
            return false;
        }
        
        // Clear any previous errors
        setFormError({
            hasError: false,
            message: "",
            field: null
        });
        
        return true;
    };
    
    // Login handler
    const handleLogin = async () => {
        // Validate form before submitting
        if (!validateForm()) {
            return;
        }
        
        setIsLoading(true);
        
        const loginData = {
            login: emailRef.current.value,
            password: passwordRef.current.value
        };
        
        try {
            const response = await axios.post(
                `${baseUrl}api/auth/login`, 
                loginData, 
                { withCredentials: true }
            );
            localStorage.setItem("accessToken", response?.data?.data?.accessToken);
            
            // Handle successful login
            setIsLoading(false);
            navigate("/");
            
        } catch (error) {
            setIsLoading(false);
            const errorMessage = error.response?.data?.message || "Login failed. Please check your credentials and try again.";
            
            setFormError({
                hasError: true,
                message: errorMessage,
                field: null
            });
            
            console.error("Login error:", error.response?.data || error.message);
        }
    };
    
    return (
        <div className="relative w-full max-w-md mx-auto px-4 md:px-6 py-14 bg-transparent backdrop-blur-sm z-10">
            {/* Background blobs */}
            {/* <div className="absolute top-1/3 -left-20 w-64 h-64 bg-amber-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div> */}
            {/* <div className="absolute top-1/3 -right-20 w-64 h-64 bg-orange-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div> */}
            {/* <div className="absolute -bottom-20 left-1/3 w-64 h-64 bg-amber-100 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div> */}
            
            <h1 className="font-bold text-2xl md:text-3xl pb-2 text-gray-800">Sign in</h1>
            <p className="text-sm md:text-base text-gray-600">Become a member - don't miss out on deals, offers, discounts and bonus vouchers.</p>

            <div className={`w-16 h-1 bg-gradient-to-r from-amber-400 to-orange-400 rounded my-4 md:my-6 transition-all duration-1000 ${isVisible ? 'w-16 opacity-100' : 'w-0 opacity-0'}`}></div>

            <div className="mt-6 space-y-4">
                <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">
                        Email <span className="text-red-500">*</span>
                    </label>
                    <input 
                        ref={emailRef}
                        type="email"
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-amber-400 ${formError.field === "email" ? 'border-red-500' : 'border-gray-300'}`}
                        required
                    />
                </div>
                
                <div className="space-y-1">
                    <label className="block text-sm font-medium text-gray-700">
                        Password <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                        <input 
                            ref={passwordRef}
                            type={passwordFieldType}
                            className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-amber-400 ${formError.field === "password" ? 'border-red-500' : 'border-gray-300'}`}
                            required
                        />
                        <button 
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-medium text-amber-500 hover:text-amber-600"
                        >
                            {showPassword ? "Hide" : "Show"}
                        </button>
                    </div>
                </div>

                <div className="flex items-center justify-between pt-2">
                    <div className="flex items-center">
                        <input 
                            type="checkbox" 
                            id="remember-me"
                            defaultChecked
                            className="h-4 w-4 text-amber-500 focus:ring-amber-400 border-gray-300 rounded" 
                        />
                        <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-700">
                            Remember me
                        </label>
                    </div>
                    <div>
                        <Link to="/forgot-password" className="text-sm font-medium text-amber-500 hover:text-amber-600">
                            Forgot Password?
                        </Link>
                    </div>
                </div>
                
                <div className="flex items-center pt-2">
                    <span className="text-sm text-gray-700">Not registered yet?</span>
                    <Link to="/signup" className="ml-2 text-sm font-medium text-amber-500 hover:text-amber-600">
                        Sign Up
                    </Link>
                </div>
                
                {formError.hasError && (
                    <div className="bg-red-50 p-3 rounded-md">
                        <p className="text-sm text-red-600">{formError.message}</p>
                    </div>
                )}
                
                <div>
                    <button 
                        onClick={handleLogin} 
                        className={`w-full bg-gradient-to-r from-amber-400 to-orange-400 text-white py-2 px-4 rounded-md transition-all duration-300 flex items-center justify-center ${isLoading ? 'opacity-70 cursor-not-allowed' : 'hover:opacity-90'}`}
                        disabled={isLoading}
                    >
                        {isLoading ?<div className="max-h-[30px] flex items-center justify-center " ><Loader /></div>: "Sign in"}
                    </button>
                </div>
            </div>
            
            <style jsx>{`
                @keyframes blob {
                    0% {
                        transform: scale(1);
                    }
                    33% {
                        transform: scale(1.1) translate(10%, 10%);
                    }
                    66% {
                        transform: scale(0.9) translate(-10%, -10%);
                    }
                    100% {
                        transform: scale(1);
                    }
                }
                .animate-blob {
                    animation: blob 7s infinite;
                }
                .animation-delay-2000 {
                    animation-delay: 2s;
                }
                .animation-delay-4000 {
                    animation-delay: 4s;
                }
            `}</style>
        </div>
    );
}

export default LoginComponent;